import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { PurchaseRequest, RequestStatus } from '../../types/request.types';
import { requestService } from '../../services/requestService';

interface RequestState {
  requests: PurchaseRequest[];
  currentRequest: PurchaseRequest | null;
  loading: boolean;
  error: string | null;
}

const initialState: RequestState = {
  requests: [],
  currentRequest: null,
  loading: false,
  error: null,
};

export const fetchRequests = createAsyncThunk(
  'request/fetchRequests',
  async () => {
    const response = await requestService.getRequests();
    return response;
  }
);

export const fetchMyRequests = createAsyncThunk(
  'request/fetchMyRequests',
  async () => {
    const response = await requestService.getMyRequests();
    return response;
  }
);

export const fetchDepartmentRequests = createAsyncThunk(
  'request/fetchDepartmentRequests',
  async () => {
    const response = await requestService.getDepartmentRequests();
    return response;
  }
);

export const createRequest = createAsyncThunk(
  'request/createRequest',
  async (data: any) => {
    const response = await requestService.createRequest(data);
    return response;
  }
);

export const updateRequestStatus = createAsyncThunk(
  'request/updateStatus',
  async ({ 
    id, 
    status, 
    comment, 
    paymentType, 
    paymentReference 
  }: { 
    id: string; 
    status: RequestStatus; 
    comment?: string; 
    paymentType?: string; 
    paymentReference?: string; 
  }) => {
    const response = await requestService.updateRequestStatus(id, status, comment, paymentType, paymentReference);
    return response;
  }
);

const requestSlice = createSlice({
  name: 'request',
  initialState,
  reducers: {
    setCurrentRequest: (state, action: PayloadAction<PurchaseRequest | null>) => {
      state.currentRequest = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch requests
      .addCase(fetchRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.requests = action.payload;
      })
      .addCase(fetchRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch requests';
      })
      
      // Fetch my requests
      .addCase(fetchMyRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.requests = action.payload;
      })
      .addCase(fetchMyRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch your requests';
      })
      
      // Fetch department requests
      .addCase(fetchDepartmentRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDepartmentRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.requests = action.payload;
      })
      .addCase(fetchDepartmentRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch department requests';
      })
      
      // Create request
      .addCase(createRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.requests.push(action.payload);
        state.currentRequest = action.payload;
      })
      .addCase(createRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create request';
      })
      
      // Update request status
      .addCase(updateRequestStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRequestStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.requests.findIndex(req => req.id === action.payload.id);
        if (index !== -1) {
          state.requests[index] = action.payload;
        }
        if (state.currentRequest?.id === action.payload.id) {
          state.currentRequest = action.payload;
        }
      })
      .addCase(updateRequestStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update request status';
      });
  },
});

export const { setCurrentRequest, clearError } = requestSlice.actions;
export default requestSlice.reducer; 